// libraries
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Typography, Space, notification } from 'antd';
import { Input, Select, DatePicker, Button } from 'antd';

// services
import * as ChristmasFriendService from '../../../../services/bb-api/christmas-friend';

// locales
import { txt } from '../../../../locales/i18n';

export function ChristmasFriendQuerySection({ setDataSource, setLoading }: any): JSX.Element {
	const initialAmount: number = 200;
	const [amount, setAmount] = useState<number>(initialAmount);
	const [userId, setUserId] = useState<string>('');
	const [dateFrom, setDateFrom] = useState<moment.Moment | null>();
	const [dateTo, setDateTo] = useState<moment.Moment | null>();
	const [searchText, setSearchText] = useState<string>('');
	const [searchTextField, setSearchTextField] = useState<string>('all');

	useEffect(() => {
		async function initialQuery(): Promise<void> {
			setLoading(true);

			try {
				const christmasFriends = await ChristmasFriendService.query({ amount: initialAmount });
				setDataSource(christmasFriends);
			} catch (error) {
				setDataSource([]);

				notification.error({
					message: (error as Error).name,
					description: (error as Error).message,
					duration: null,
				});
			}

			setLoading(false);
		}

		initialQuery();
	}, [setLoading, setDataSource]);

	async function query(): Promise<void> {
		setLoading(true);

		const queryObject: any = {
			amount,
			userId,
			searchText,
			dateFrom: dateFrom?.format('YYYY-MM-DD'),
			dateTo: dateTo?.format('YYYY-MM-DD'),
		};

		if (searchTextField === 'all') {
			queryObject.searchFields = ['email', 'username', 'municipality'];
		} else {
			queryObject.searchFields = [searchTextField];
		}

		try {
			const christmasFriends = await ChristmasFriendService.query(queryObject);
			setDataSource(christmasFriends);
		} catch (error) {
			setDataSource([]);

			notification.error({
				message: (error as Error).name,
				description: (error as Error).message,
				duration: null,
			});
		}

		setLoading(false);
	}

	return (
		<Container>
			<Space direction="vertical" size={4}>
				<Typography.Text type="secondary">{txt('global.user id')}</Typography.Text>
				<Input
					style={{ width: 'fit-content' }}
					defaultValue={userId}
					placeholder={txt('global.user id')}
					onChange={({ target }) => setUserId(target.value)}
					allowClear
				/>
			</Space>

			<Space direction="vertical" size={4}>
				<Typography.Text type="secondary">{txt('global.search')}</Typography.Text>
				<Input.Group compact>
					<Select defaultValue={searchTextField} onChange={(value: string) => setSearchTextField(value)}>
						<Select.Option value="all">{txt('global.all')}</Select.Option>
						<Select.Option value="email">Email</Select.Option>
						<Select.Option value="username">{txt('global.username')}</Select.Option>
						<Select.Option value="municipality">{txt('global.municipality')}</Select.Option>
					</Select>
					<Input
						style={{ width: 'fit-content' }}
						defaultValue={searchText}
						onChange={({ target }) => setSearchText(target.value)}
						placeholder={txt('global.search')}
						allowClear
					/>
				</Input.Group>
			</Space>

			<Space direction="vertical" size={4}>
				<Typography.Text type="secondary">{txt('global.date')}</Typography.Text>
				<Input.Group compact>
					<DatePicker
						value={dateFrom}
						onChange={(value) => setDateFrom(value)}
						placeholder={txt('global.from')}
					/>
					<DatePicker value={dateTo} onChange={(value) => setDateTo(value)} placeholder={txt('global.to')} />
				</Input.Group>
			</Space>

			<Space direction="vertical" size={4}>
				<Typography.Text type="secondary">{txt('global.amount')}</Typography.Text>
				<Select defaultValue={amount} onChange={(value: number) => setAmount(value)}>
					<Select.Option value={200}>200</Select.Option>
					<Select.Option value={500}>500</Select.Option>
					<Select.Option value={1000}>1000</Select.Option>
				</Select>
			</Space>

			<Button type="primary" onClick={query}>
				{txt('global.search')}
			</Button>
		</Container>
	);
}

const Container = styled.div`
	padding: 1rem 2rem;
	display: flex;
	flex-wrap: wrap;
	gap: 1rem;
	justify-content: center;
	align-items: end;

	.ant-typography {
		font-size: 0.6rem;
		font-weight: bold;
	}
`;
